import React, { useState, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import api from '../lib/api'
import { getUserTimezone } from '../lib/timezone'
import { formatPhoneDisplay } from '@/lib/utils'
import { onSocketEvent } from '../hooks/useWebSocket'
import { useToast } from '@/hooks/use-toast'
import { useFeatureGate } from '@/hooks/useFeatureGate'
import {
  Phone,
  PhoneIncoming,
  PhoneOutgoing,
  PhoneMissed,
  Clock,
  PlayCircle,
  FileText,
  MoreHorizontal,
  Search,
  Filter,
  PhoneCall,
  XCircle,
  Trash2,
  AlertCircle,
  ArrowUpDown,
  Pencil,
  PhoneForwarded,
  Loader2,
  CheckCircle2,
  Voicemail
} from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from '@/components/ui/dialog'
import { ScrollArea } from '@/components/ui/scroll-area'
import { PageHeader, StatCard, EmptyState } from '@/components/dashboard-ui'

interface VoiceCall {
  id: string
  direction: 'inbound' | 'outbound'
  status: 'initiated' | 'ringing' | 'in_progress' | 'completed' | 'failed' | 'missed' | 'voicemail' | 'cancelled'
  phoneNumberFrom: string
  phoneNumberTo: string
  callerName: string | null
  durationSeconds: number | null
  recordingUrl: string | null
  transcriptionText: string | null
  aiSummary: string | null
  sentiment: 'positive' | 'neutral' | 'negative' | null
  qualificationScore: number | null
  callbackStatus: 'none' | 'needed' | 'queued' | 'completed' | 'resolved' | 'dismissed' | null
  callbackReason: string | null
  transferStatus: 'none' | 'transferred' | null
  transferredTo: string | null
  callType: 'new_lead' | 'existing_client' | 'spam' | 'solicitation' | 'general_inquiry' | 'wrong_number' | null
  metadata: Record<string, any> | null
  createdAt: string
}

interface VoiceStats {
  monthly: {
    totalCalls: number
    inbound: number
    outbound: number
    completed: number
    missed: number
    outboundCompleted: number
    totalMinutes: number
  }
  weekly: {
    totalCalls: number
    totalMinutes: number
  }
  daily: {
    totalCalls: number
    totalMinutes: number
  }
}

export default function CallsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const agentIdFilter = searchParams.get('agentId')
  const agentNameFilter = searchParams.get('agentName')
  // Deep-link target: when /calls?callId=<id> is opened (e.g. from a Lead's
  // timeline), auto-open the call detail dialog. The call may live outside
  // the default "today" window, so we widen the date filter on first load
  // when this param is present.
  const callIdParam = searchParams.get('callId')
  const { toast } = useToast()
  // auto_callback is Principal-only (also unlockable during trial via
  // FeatureGateService.TRIAL_UNLOCKABLE_FEATURES). Hide queue/dismiss
  // dropdown items when the org isn't entitled — backend already 402s,
  // but the UI shouldn't dangle the option in front of Associate users.
  const { isAllowed } = useFeatureGate()
  const callbackAllowed = isAllowed('auto_callback')
  const [calls, setCalls] = useState<VoiceCall[]>([])
  const [stats, setStats] = useState<VoiceStats | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [searchInput, setSearchInput] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const callsListRef = useRef<HTMLDivElement | null>(null)
  const [dateFilter, setDateFilter] = useState<string>(
    searchParams.get('date') || (callIdParam ? 'all' : 'today')
  )
  const [directionFilter, setDirectionFilter] = useState<string>('all')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [callbackFilter, setCallbackFilter] = useState<string>(searchParams.get('callbackStatus') || 'all')
  const [callTypeFilter, setCallTypeFilter] = useState<string>('all')
  const bookingFilter = searchParams.get('bookings') === 'true'
  const [selectedCall, setSelectedCall] = useState<VoiceCall | null>(null)
  const [sortField, setSortField] = useState<string>('createdAt')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')
  const [showTranscript, setShowTranscript] = useState(false)
  const [total, setTotal] = useState(0)
  const [offset, setOffset] = useState(0)
  const [pendingCallbacks, setPendingCallbacks] = useState(0)
  const [showDialDialog, setShowDialDialog] = useState(false)
  const [dialNumber, setDialNumber] = useState('')
  const [editingCallerId, setEditingCallerId] = useState<string | null>(null)
  const [editCallerValue, setEditCallerValue] = useState('')
  const [orgTimezone, setOrgTimezone] = useState<string>(getUserTimezone())
  const limit = 50

  const formatCallTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true,
      timeZone: orgTimezone
    })
  }

  useEffect(() => {
    loadCalls()
    loadStats()
  }, [directionFilter, statusFilter, callbackFilter, callTypeFilter, dateFilter, searchQuery, sortField, sortDir, offset])

  // Deep-link: ?callId=<id> auto-opens the matching call once the list
  // contains it. Strip the param after opening so a refresh doesn't keep
  // re-opening the dialog if the user closes it.
  useEffect(() => {
    if (!callIdParam) return
    const match = calls.find((c) => c.id === callIdParam)
    if (!match) return
    setSelectedCall(match)
    setShowTranscript(true)
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.delete('callId')
      return next
    }, { replace: true })
  }, [callIdParam, calls, setSearchParams])

  // Real-time updates via Socket.IO when call events arrive from webhooks
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>
    const cleanup = onSocketEvent('call:updated', () => {
      clearTimeout(timer)
      timer = setTimeout(() => {
        loadCalls()
        loadStats()
      }, 200)
    })
    return () => {
      cleanup()
      clearTimeout(timer)
    }
  }, [directionFilter, statusFilter, callbackFilter, dateFilter, offset])

  const loadCalls = async () => {
    setIsLoading(true)
    try {
      const params = new URLSearchParams()
      params.append('limit', limit.toString())
      params.append('offset', offset.toString())
      if (directionFilter !== 'all') params.append('direction', directionFilter)
      if (statusFilter !== 'all') params.append('status', statusFilter)
      if (callbackFilter !== 'all') params.append('callbackStatus', callbackFilter)
      if (agentIdFilter) params.append('assistantId', agentIdFilter)
      if (bookingFilter) params.append('bookingMade', 'true')
      if (callTypeFilter !== 'all') params.append('callType', callTypeFilter)
      if (searchQuery.trim()) params.append('search', searchQuery.trim())
      params.append('sort', sortField)
      params.append('sortDir', sortDir)
      if (dateFilter !== 'all') {
        const startDate = new Date()
        if (dateFilter === 'today') {
          startDate.setHours(0, 0, 0, 0)
        } else if (dateFilter === 'yesterday') {
          startDate.setDate(startDate.getDate() - 1)
          startDate.setHours(0, 0, 0, 0)
          const endDate = new Date(startDate)
          endDate.setDate(endDate.getDate() + 1)
          params.append('endDate', endDate.toISOString())
        } else if (dateFilter === '7d') {
          startDate.setDate(startDate.getDate() - 7)
        } else if (dateFilter === '30d') {
          startDate.setDate(startDate.getDate() - 30)
        }
        params.append('startDate', startDate.toISOString())
      }

      const response = await api.get(`/api/voice/calls?${params.toString()}`)
      if (response.data.success) {
        setCalls(response.data.calls)
        setTotal(response.data.total)
        if (response.data.timezone) setOrgTimezone(response.data.timezone)
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load calls. Please refresh the page.', variant: 'destructive' })
    } finally {
      setIsLoading(false)
    }
  }

  const loadPendingCallbackCount = async () => {
    try {
      const response = await api.get('/api/voice/callbacks?limit=1')
      if (response.data.success) {
        const s = response.data.summary || {}
        setPendingCallbacks((s.needed || 0) + (s.queued || 0))
      }
    } catch { /* non-critical */ }
  }

  const handleQueueCallback = async (callId: string) => {
    try {
      await api.post(`/api/voice/calls/${callId}/queue-callback`)
      toast({ title: 'Callback queued', description: 'The callback has been added to the queue.' })
      loadCalls()
      loadPendingCallbackCount()
    } catch (error: any) {
      toast({ title: 'Failed to queue callback', description: error.response?.data?.message || 'Please try again.', variant: 'destructive' })
    }
  }

  const handleDismissCallback = async (callId: string) => {
    try {
      await api.post(`/api/voice/calls/${callId}/dismiss-callback`)
      toast({ title: 'Callback dismissed' })
      loadCalls()
      loadPendingCallbackCount()
    } catch (error: any) {
      toast({ title: 'Failed to dismiss callback', description: error.response?.data?.message || 'Please try again.', variant: 'destructive' })
    }
  }

  const handleMarkMissed = async (callId: string) => {
    try {
      await api.post(`/api/voice/calls/${callId}/mark-missed`)
      toast({ title: 'Call marked as missed' })
      loadCalls()
    } catch (error: any) {
      toast({ title: 'Failed to mark call', description: error.response?.data?.message || 'Please try again.', variant: 'destructive' })
    }
  }

  const handleDeleteCall = async (callId: string) => {
    try {
      await api.delete(`/api/voice/calls/${callId}`)
      toast({ title: 'Call deleted' })
      loadCalls()
      loadStats()
    } catch (error: any) {
      toast({ title: 'Failed to delete call', description: error.response?.data?.message || 'Please try again.', variant: 'destructive' })
    }
  }

  const loadStats = async () => {
    try {
      const params = new URLSearchParams()
      params.append('dateFilter', dateFilter)
      // Forward the same filters the call list uses so the cards match what's shown below
      if (agentIdFilter) params.append('assistantId', agentIdFilter)
      if (directionFilter !== 'all') params.append('direction', directionFilter)
      if (callTypeFilter !== 'all') params.append('callType', callTypeFilter)
      if (bookingFilter) params.append('bookingMade', 'true')
      const [statsRes] = await Promise.all([
        api.get(`/api/voice/stats?${params.toString()}`),
        loadPendingCallbackCount()
      ])
      if (statsRes.data.success) {
        setStats(statsRes.data.stats)
        if (statsRes.data.timezone) setOrgTimezone(statsRes.data.timezone)
      }
    } catch (error) {
      console.error('Failed to load voice stats:', error)
      toast({ title: 'Error', description: 'Failed to load call stats', variant: 'destructive' })
    }
  }

  const formatDuration = (seconds: number | null): string => {
    if (!seconds) return 'N/A'
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const formatPhoneNumber = (phone: string) => formatPhoneDisplay(phone, 'Unknown')

  const isProcessingInsights = (call: VoiceCall): boolean => {
    if (call.aiSummary) return false
    if (call.status !== 'completed' && call.status !== 'voicemail') return false
    const ageMs = Date.now() - new Date(call.createdAt).getTime()
    return ageMs < 5 * 60 * 1000
  }

  const getStatusBadge = (status: string, createdAt?: string) => {
    if (status === 'in_progress') {
      return <Badge className="bg-green-500 text-white hover:bg-green-600 animate-pulse">Live</Badge>
    }
    if (status === 'ringing') {
      return <Badge className="bg-amber-500 text-white hover:bg-amber-600 animate-pulse">Ringing</Badge>
    }
    if (status === 'initiated' && createdAt) {
      const ageMs = Date.now() - new Date(createdAt).getTime()
      if (ageMs > 60 * 60 * 1000) return <Badge variant="destructive" className="text-xs">Stale</Badge>
    }
    const variants: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline'; label: string }> = {
      completed: { variant: 'default', label: 'Completed' },
      initiated: { variant: 'outline', label: 'Dialing' },
      missed: { variant: 'destructive', label: 'Missed' },
      failed: { variant: 'destructive', label: 'Failed' },
      voicemail: { variant: 'outline', label: 'Voicemail' },
      cancelled: { variant: 'outline', label: 'Cancelled' },
      forwarded: { variant: 'secondary', label: 'Forwarded' },
      queued: { variant: 'outline', label: 'Queued' }
    }
    const config = variants[status] || { variant: 'outline' as const, label: status }
    return <Badge variant={config.variant}>{config.label}</Badge>
  }

  const getDirectionIcon = (direction: string, status: string) => {
    if (status === 'missed') return <PhoneMissed className="h-4 w-4 text-destructive" />
    if (direction === 'inbound') return <PhoneIncoming className="h-4 w-4 text-blue-500" />
    return <PhoneOutgoing className="h-4 w-4 text-green-500" />
  }

  const getCallbackBadge = (callbackStatus: string | null, callbackReason: string | null) => {
    if (!callbackStatus || callbackStatus === 'none') return null
    const reasonLabels: Record<string, string> = {
      voicemail: 'Voicemail',
      no_answer: 'No Answer',
      incomplete_intake: 'Incomplete',
      outbound_voicemail: 'Voicemail (Out)',
      outbound_no_answer: 'No Answer (Out)',
      manual: 'Manual'
    }
    const label = reasonLabels[callbackReason || ''] || 'Callback'
    if (callbackStatus === 'needed') {
      // Action-needed: red, with alert icon — should not read as "done"
      return <Badge variant="destructive" className="text-xs gap-1 whitespace-nowrap"><AlertCircle className="h-3 w-3" />{label}</Badge>
    }
    if (callbackStatus === 'queued') {
      // Pending action: amber, phone icon
      return <Badge className="text-xs gap-1 whitespace-nowrap bg-amber-500 hover:bg-amber-500 text-white"><PhoneCall className="h-3 w-3" />Queued</Badge>
    }
    if (callbackStatus === 'completed' || callbackStatus === 'resolved') {
      // Callback made successfully: emerald with check — distinct from the neutral "Completed" status badge
      return <Badge className="text-xs gap-1 whitespace-nowrap bg-emerald-600 hover:bg-emerald-600 text-white"><CheckCircle2 className="h-3 w-3" />Callback</Badge>
    }
    if (callbackStatus === 'dismissed') {
      return <Badge variant="outline" className="text-xs text-muted-foreground">Dismissed</Badge>
    }
    return null
  }

  const getTransferBadge = (transferStatus: string | null, transferredTo: string | null) => {
    if (!transferStatus || transferStatus === 'none') return null
    return (
      <Badge variant="outline" className="text-xs gap-1">
        <PhoneForwarded className="h-3 w-3" />
        {transferredTo ? `Transferred to ${formatPhoneNumber(transferredTo)}` : 'Transferred'}
      </Badge>
    )
  }

  const getScoreBadge = (score: number | null, sentiment: string | null) => {
    if (score == null) return <span className="text-muted-foreground">--</span>
    const color = score >= 7 ? 'bg-green-500' : score >= 4 ? 'bg-yellow-500' : 'bg-red-500'
    const s = sentiment?.toLowerCase()
    const sentimentIcon = s === 'positive' ? '+' : s === 'negative' ? '-' : '~'
    return (
      <span className="inline-flex items-center gap-1.5">
        <span className={`inline-flex items-center justify-center ${color} text-white text-xs font-semibold rounded-full px-2 py-0.5`}>
          {score}/10
        </span>
        <span className="text-xs text-muted-foreground">{sentimentIcon}</span>
      </span>
    )
  }

  const CALL_TYPE_LABELS: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
    new_lead: { label: 'New Lead', variant: 'default' },
    existing_client: { label: 'Existing Client', variant: 'secondary' },
    general_inquiry: { label: 'General', variant: 'outline' },
    spam: { label: 'Spam', variant: 'destructive' },
    solicitation: { label: 'Solicitation', variant: 'destructive' },
    wrong_number: { label: 'Wrong #', variant: 'destructive' },
  }

  const getCallTypeBadge = (callType: string | null) => {
    if (!callType) return null
    const config = CALL_TYPE_LABELS[callType]
    if (!config) return null
    return <Badge variant={config.variant}>{config.label}</Badge>
  }

  const handleSaveCallerName = async (callId: string) => {
    if (!editCallerValue.trim()) { setEditingCallerId(null); return }
    try {
      const res = await api.put(`/api/voice/calls/${callId}/caller-name`, { name: editCallerValue.trim() })
      const phone = res.data.phone
      const trimmedName = editCallerValue.trim()
      // Update ALL calls with the same phone number in local state
      setCalls(prev => prev.map(c => {
        const callerPhone = c.direction === 'inbound' ? c.phoneNumberFrom : c.phoneNumberTo
        if (phone && callerPhone === phone) {
          return { ...c, callerName: trimmedName, metadata: { ...c.metadata, callerName: trimmedName } }
        }
        return c
      }))
      setEditingCallerId(null)
      const count = res.data.updatedCount
      if (count > 1) {
        toast({ title: 'Name saved', description: `Updated ${count} calls from this number.` })
      }
    } catch {
      toast({ title: 'Error', description: 'Failed to save caller name.', variant: 'destructive' })
    }
  }

  const getCallerDisplayName = (call: VoiceCall): string | null => {
    return call.callerName || call.metadata?.callerName || null
  }

  const toggleSort = (field: string) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDir('desc')
    }
    setOffset(0)
  }

  // Sort and filter are now server-side — calls array is already sorted/filtered
  const filteredCalls = calls

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 bg-muted animate-pulse rounded" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-32 bg-muted animate-pulse rounded-lg" />
          ))}
        </div>
        <div className="h-96 bg-muted animate-pulse rounded-lg" />
      </div>
    )
  }

  return (
    <div className="space-y-5 sm:space-y-6">
      <PageHeader
        eyebrow="Activity"
        title="Voice calls"
        subtitle={bookingFilter ? 'Showing calls where a booking was made'
          : agentNameFilter ? `Showing calls for ${agentNameFilter}`
          : dateFilter === 'today' ? "Showing today's calls"
          : dateFilter === 'yesterday' ? "Showing yesterday's calls"
          : dateFilter === '7d' ? 'Showing calls from the last 7 days'
          : dateFilter === '30d' ? 'Showing calls from the last 30 days'
          : 'Inbound, outbound, and AI follow-ups across your firm.'}
        icon={
          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-brand-royal/20 to-brand-periwinkle/15 border border-brand-royal/20 flex items-center justify-center">
            <Phone className="h-5 w-5 text-brand-royal" />
          </div>
        }
        actions={
          <>
            <Select value={dateFilter} onValueChange={v => { setDateFilter(v); setOffset(0) }}>
              <SelectTrigger className="w-[140px] sm:w-[160px]">
                <Clock className="mr-2 h-4 w-4" />
                <SelectValue placeholder="Time Range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Time</SelectItem>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="yesterday">Yesterday</SelectItem>
                <SelectItem value="7d">Last 7 Days</SelectItem>
                <SelectItem value="30d">Last 30 Days</SelectItem>
              </SelectContent>
            </Select>
            {(agentIdFilter || bookingFilter) && (
              <Button variant="outline" onClick={() => setSearchParams({})}>
                <XCircle className="mr-2 h-4 w-4" />
                Clear filter
              </Button>
            )}
            <Button onClick={() => setShowDialDialog(true)} className="brand-gradient text-white hover:opacity-90 shadow-md shadow-brand-periwinkle/20">
              <Phone className="mr-2 h-4 w-4" />
              New call
            </Button>
          </>
        }
      />

      {/* Stats — Bento layout: 1 prominent + 3 supporting (anti 4-equal-cards) */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 stagger-fade">
          <StatCard
            className="col-span-2 lg:col-span-2 lg:row-span-2"
            label={dateFilter === 'today' ? "Today's calls" : dateFilter === 'yesterday' ? "Yesterday's calls" : dateFilter === '7d' ? 'Calls (7 days)' : dateFilter === '30d' ? 'Calls (30 days)' : 'Monthly calls'}
            value={stats.monthly.totalCalls}
            icon={Phone}
            tone="royal"
            hint={`${stats.monthly.totalMinutes} minutes used`}
          />
          <StatCard
            label="Calls answered"
            value={stats.monthly.inbound}
            icon={PhoneIncoming}
            tone="periwinkle"
            hint={`${stats.monthly.missed} missed`}
          />
          <StatCard
            label="Follow-up calls"
            value={stats.monthly.outbound}
            icon={PhoneOutgoing}
            tone="green"
            hint={`${stats.monthly.outboundCompleted} completed`}
          />
          <StatCard
            className="col-span-2 lg:col-span-2"
            label="Today"
            value={stats.daily.totalCalls}
            icon={Clock}
            tone="amber"
            hint={`${stats.daily.totalMinutes} minutes`}
          />
        </div>
      )}

      {/* Callbacks Needed Banner */}
      {pendingCallbacks > 0 && (
        <Card className="border-orange-300 bg-orange-50 dark:bg-orange-950/20">
          <CardContent className="py-3 flex items-center justify-between">
            <div className="flex items-center gap-2 text-orange-700 dark:text-orange-400">
              <AlertCircle className="h-4 w-4" />
              <span className="text-sm font-medium">
                {pendingCallbacks} call{pendingCallbacks !== 1 ? 's' : ''} need{pendingCallbacks === 1 ? 's' : ''} a callback
              </span>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="border-orange-300 text-orange-700 hover:bg-orange-100"
              onClick={() => {
                // Pending callbacks aren't date-bound — a missed call from
                // last week still needs follow-up today. Widen the date
                // filter and clear other filters that could hide rows.
                setCallbackFilter('pending')
                setDateFilter('all')
                setDirectionFilter('all')
                setStatusFilter('all')
                setSearchInput('')
                setSearchQuery('')
                setOffset(0)
                requestAnimationFrame(() => {
                  callsListRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                })
              }}
            >
              View All
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row sm:flex-wrap gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or phone number..."
                value={searchInput}
                onChange={(e) => {
                  setSearchInput(e.target.value)
                  if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
                  searchTimerRef.current = setTimeout(() => {
                    setSearchQuery(e.target.value)
                    setOffset(0)
                  }, 400)
                }}
                className="pl-10"
              />
            </div>
            <Select value={directionFilter} onValueChange={setDirectionFilter}>
              <SelectTrigger className="w-[180px]">
                <Filter className="mr-2 h-4 w-4" />
                <SelectValue placeholder="Direction" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Directions</SelectItem>
                <SelectItem value="inbound">Incoming</SelectItem>
                <SelectItem value="outbound">Follow-Up</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="missed">Missed</SelectItem>
                <SelectItem value="voicemail">Voicemail</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>
            <Select value={callbackFilter} onValueChange={v => { setCallbackFilter(v); setOffset(0) }}>
              <SelectTrigger className="w-[180px]">
                <PhoneCall className="mr-2 h-4 w-4" />
                <SelectValue placeholder="Callback" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Callbacks</SelectItem>
                <SelectItem value="pending">Follow-ups Pending</SelectItem>
                <SelectItem value="needed">Needs Callback</SelectItem>
                <SelectItem value="queued">Callback Queued</SelectItem>
                <SelectItem value="completed">Called Back</SelectItem>
                <SelectItem value="resolved">Called Back (Self)</SelectItem>
                <SelectItem value="dismissed">Dismissed</SelectItem>
              </SelectContent>
            </Select>
            <Select value={callTypeFilter} onValueChange={setCallTypeFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Call Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="new_lead">New Lead</SelectItem>
                <SelectItem value="existing_client">Existing Client</SelectItem>
                <SelectItem value="general_inquiry">General Inquiry</SelectItem>
                <SelectItem value="spam">Spam</SelectItem>
                <SelectItem value="solicitation">Solicitation</SelectItem>
                <SelectItem value="wrong_number">Wrong #</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Calls list anchor — banner's "View All" scrolls here */}
      <div ref={callsListRef} />

      {/* Calls — Mobile Cards */}
      <div className="sm:hidden space-y-2">
        {filteredCalls.length === 0 ? (
          <EmptyState
            icon={PhoneCall}
            tone={callbackFilter !== 'all' || directionFilter !== 'all' || statusFilter !== 'all' ? 'neutral' : 'periwinkle'}
            compact
            title={callbackFilter !== 'all' || directionFilter !== 'all' || statusFilter !== 'all' ? 'No matching calls' : 'No calls yet'}
            description={callbackFilter !== 'all' || directionFilter !== 'all' || statusFilter !== 'all'
              ? 'Try adjusting your filters.'
              : 'Calls will appear here as they come in.'}
          />
        ) : (
          filteredCalls.map((call) => {
            const callerName = getCallerDisplayName(call)
            const phone = call.direction === 'inbound' ? call.phoneNumberFrom : call.phoneNumberTo
            return (
              <Card key={call.id} className="p-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    {getDirectionIcon(call.direction, call.status)}
                    <div className="min-w-0">
                      <p className="font-medium text-sm truncate">
                        {callerName || formatPhoneNumber(phone)}
                      </p>
                      {callerName && (
                        <p className="text-xs text-muted-foreground truncate">{formatPhoneNumber(phone)}</p>
                      )}
                      <p className="text-xs text-muted-foreground">{formatCallTime(call.createdAt)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {getStatusBadge(call.status, call.createdAt)}
                    <span className="text-xs text-muted-foreground">{formatDuration(call.durationSeconds)}</span>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {call.recordingUrl && (
                          <DropdownMenuItem onClick={() => { if (call.recordingUrl) window.open(call.recordingUrl, '_blank') }}>
                            <PlayCircle className="mr-2 h-4 w-4" />
                            Play Recording
                          </DropdownMenuItem>
                        )}
                        {call.transcriptionText ? (
                          <DropdownMenuItem onClick={() => {
                            setSelectedCall(call)
                            setShowTranscript(true)
                          }}>
                            <FileText className="mr-2 h-4 w-4" />
                            View Transcript
                          </DropdownMenuItem>
                        ) : isProcessingInsights(call) ? (
                          <DropdownMenuItem disabled>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Processing Transcript...
                          </DropdownMenuItem>
                        ) : null}
                        {callbackAllowed && (!call.callbackStatus || call.callbackStatus === 'none' || call.callbackStatus === 'dismissed') && (
                          <DropdownMenuItem onClick={() => handleQueueCallback(call.id)}>
                            <PhoneCall className="mr-2 h-4 w-4" />
                            Queue Callback
                          </DropdownMenuItem>
                        )}
                        {callbackAllowed && (call.callbackStatus === 'needed' || call.callbackStatus === 'queued') && (
                          <>
                            <DropdownMenuItem onClick={() => handleQueueCallback(call.id)}>
                              <PhoneCall className="mr-2 h-4 w-4" />
                              Re-queue Callback
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDismissCallback(call.id)} className="text-destructive">
                              <XCircle className="mr-2 h-4 w-4" />
                              Dismiss Callback
                            </DropdownMenuItem>
                          </>
                        )}
                        {(call.status === 'ringing' || call.status === 'initiated' || call.status === 'in_progress') && (
                          <DropdownMenuItem onClick={() => handleMarkMissed(call.id)} className="text-destructive">
                            <XCircle className="mr-2 h-4 w-4" />
                            Mark as Missed
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={() => handleDeleteCall(call.id)} className="text-destructive">
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete Call
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
                {call.status === 'voicemail' && call.recordingUrl && (
                  <div className="mt-2 flex items-center gap-2 rounded-md bg-violet-500/5 border border-violet-500/20 px-2 py-1.5">
                    <Voicemail className="h-3.5 w-3.5 text-violet-600 shrink-0" />
                    <audio
                      controls
                      preload="none"
                      src={call.recordingUrl}
                      className="h-8 w-full"
                    />
                  </div>
                )}
              </Card>
            )
          })
        )}
      </div>

      {/* Calls Table — Desktop */}
      <Card className="hidden sm:block">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[50px]">Type</TableHead>
                <TableHead>From</TableHead>
                <TableHead className="hidden sm:table-cell">To</TableHead>
                <TableHead className="cursor-pointer hover:text-foreground" onClick={() => toggleSort('status')}>
                  Status <ArrowUpDown className="inline w-3 h-3 ml-1" />
                </TableHead>
                <TableHead className="hidden md:table-cell">Callback</TableHead>
                <TableHead className="hidden sm:table-cell cursor-pointer hover:text-foreground" onClick={() => toggleSort('duration')}>
                  Duration <ArrowUpDown className="inline w-3 h-3 ml-1" />
                </TableHead>
                <TableHead className="hidden lg:table-cell cursor-pointer hover:text-foreground" onClick={() => toggleSort('qualificationScore')}>
                  Score <ArrowUpDown className="inline w-3 h-3 ml-1" />
                </TableHead>
                <TableHead className="hidden lg:table-cell">Type</TableHead>
                <TableHead className="cursor-pointer hover:text-foreground" onClick={() => toggleSort('createdAt')}>
                  Date <ArrowUpDown className="inline w-3 h-3 ml-1" />
                </TableHead>
                <TableHead className="w-[80px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredCalls.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} className="p-2">
                    <EmptyState
                      icon={PhoneCall}
                      tone={callbackFilter !== 'all' || directionFilter !== 'all' || statusFilter !== 'all' ? 'neutral' : 'periwinkle'}
                      title={callbackFilter !== 'all' || directionFilter !== 'all' || statusFilter !== 'all' ? 'No matching calls' : 'No calls yet'}
                      description={callbackFilter !== 'all' || directionFilter !== 'all' || statusFilter !== 'all'
                        ? 'Try adjusting your filters to see more results.'
                        : 'Calls will appear here as they come in. Your AI receptionist handles them 24/7.'}
                    />
                  </TableCell>
                </TableRow>
              ) : (
                filteredCalls.map((call) => (
                  <React.Fragment key={call.id}>
                  <TableRow>
                    <TableCell>
                      {getDirectionIcon(call.direction, call.status)}
                    </TableCell>
                    <TableCell>
                      <button
                        onClick={() => {
                          if ('ontouchstart' in window || navigator.maxTouchPoints > 0) {
                            window.open(`tel:${call.phoneNumberFrom}`, '_self')
                          } else {
                            navigator.clipboard.writeText(call.phoneNumberFrom)
                            toast({ title: 'Copied', description: `${formatPhoneNumber(call.phoneNumberFrom)} copied to clipboard` })
                          }
                        }}
                        title="Click to copy (tap to call on mobile)"
                        className="hover:text-primary hover:underline transition-colors text-left"
                      >
                        {formatPhoneNumber(call.phoneNumberFrom)}
                      </button>
                      {call.direction === 'inbound' && (
                        <div className="mt-0.5">
                          {editingCallerId === call.id ? (
                            <input
                              value={editCallerValue}
                              onChange={(e) => setEditCallerValue(e.target.value)}
                              onKeyDown={(e) => { if (e.key === 'Enter') handleSaveCallerName(call.id); if (e.key === 'Escape') setEditingCallerId(null) }}
                              onBlur={() => handleSaveCallerName(call.id)}
                              className="h-6 w-32 px-1.5 text-xs border rounded bg-background"
                              autoFocus
                            />
                          ) : (
                            <button
                              onClick={() => { setEditingCallerId(call.id); setEditCallerValue(getCallerDisplayName(call) || '') }}
                              className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1"
                            >
                              {getCallerDisplayName(call) || <>Add name <Pencil className="h-2.5 w-2.5" /></>}
                            </button>
                          )}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">
                      <button
                        onClick={() => {
                          if ('ontouchstart' in window || navigator.maxTouchPoints > 0) {
                            window.open(`tel:${call.phoneNumberTo}`, '_self')
                          } else {
                            navigator.clipboard.writeText(call.phoneNumberTo)
                            toast({ title: 'Copied', description: `${formatPhoneNumber(call.phoneNumberTo)} copied to clipboard` })
                          }
                        }}
                        title="Click to copy (tap to call on mobile)"
                        className="hover:text-primary hover:underline transition-colors text-left"
                      >
                        {formatPhoneNumber(call.phoneNumberTo)}
                      </button>
                      {call.direction === 'outbound' && (
                        <div className="mt-0.5">
                          {editingCallerId === call.id ? (
                            <input
                              value={editCallerValue}
                              onChange={(e) => setEditCallerValue(e.target.value)}
                              onKeyDown={(e) => { if (e.key === 'Enter') handleSaveCallerName(call.id); if (e.key === 'Escape') setEditingCallerId(null) }}
                              onBlur={() => handleSaveCallerName(call.id)}
                              className="h-6 w-full max-w-32 px-1.5 text-xs border rounded bg-background"
                              autoFocus
                            />
                          ) : (
                            <button
                              onClick={() => { setEditingCallerId(call.id); setEditCallerValue(getCallerDisplayName(call) || '') }}
                              className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1"
                            >
                              {getCallerDisplayName(call) || <>Add name <Pencil className="h-2.5 w-2.5" /></>}
                            </button>
                          )}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(call.status, call.createdAt)}
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <div className="flex flex-col gap-1">
                        {getCallbackBadge(call.callbackStatus, call.callbackReason)}
                        {getTransferBadge(call.transferStatus, call.transferredTo)}
                        {!getCallbackBadge(call.callbackStatus, call.callbackReason) && !getTransferBadge(call.transferStatus, call.transferredTo) && (
                          <span className="text-muted-foreground text-xs">—</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">
                      {formatDuration(call.durationSeconds)}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      {isProcessingInsights(call)
                        ? <Badge variant="outline" className="text-xs gap-1 animate-pulse"><Loader2 className="h-3 w-3 animate-spin" />Analyzing</Badge>
                        : getScoreBadge(call.qualificationScore, call.sentiment)}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      {getCallTypeBadge(call.callType)}
                    </TableCell>
                    <TableCell>
                      {formatCallTime(call.createdAt)}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {call.recordingUrl && (
                            <DropdownMenuItem onClick={() => { if (call.recordingUrl) window.open(call.recordingUrl, '_blank') }}>
                              <PlayCircle className="mr-2 h-4 w-4" />
                              Play Recording
                            </DropdownMenuItem>
                          )}
                          {call.transcriptionText ? (
                            <DropdownMenuItem onClick={() => {
                              setSelectedCall(call)
                              setShowTranscript(true)
                            }}>
                              <FileText className="mr-2 h-4 w-4" />
                              View Transcript
                            </DropdownMenuItem>
                          ) : isProcessingInsights(call) ? (
                            <DropdownMenuItem disabled>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Processing Transcript...
                            </DropdownMenuItem>
                          ) : null}
                          {callbackAllowed && (!call.callbackStatus || call.callbackStatus === 'none' || call.callbackStatus === 'dismissed') && (
                            <DropdownMenuItem onClick={() => handleQueueCallback(call.id)}>
                              <PhoneCall className="mr-2 h-4 w-4" />
                              Queue Callback
                            </DropdownMenuItem>
                          )}
                          {callbackAllowed && (call.callbackStatus === 'needed' || call.callbackStatus === 'queued') && (
                            <>
                              <DropdownMenuItem onClick={() => handleQueueCallback(call.id)}>
                                <PhoneCall className="mr-2 h-4 w-4" />
                                Re-queue Callback
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDismissCallback(call.id)} className="text-destructive">
                                <XCircle className="mr-2 h-4 w-4" />
                                Dismiss Callback
                              </DropdownMenuItem>
                            </>
                          )}
                          {(call.status === 'ringing' || call.status === 'initiated' || call.status === 'in_progress') && (
                            <DropdownMenuItem onClick={() => handleMarkMissed(call.id)} className="text-destructive">
                              <XCircle className="mr-2 h-4 w-4" />
                              Mark as Missed
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => handleDeleteCall(call.id)} className="text-destructive">
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete Call
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                  {call.status === 'voicemail' && call.recordingUrl && (
                    <TableRow className="bg-muted/20 hover:bg-muted/20">
                      <TableCell colSpan={10} className="py-2">
                        <div className="flex items-center gap-2 pl-12">
                          <Voicemail className="h-3.5 w-3.5 text-violet-600 shrink-0" />
                          <span className="text-xs text-muted-foreground shrink-0">Voicemail:</span>
                          <audio
                            controls
                            preload="none"
                            src={call.recordingUrl}
                            className="h-8 max-w-md w-full"
                          />
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      {total > limit && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {offset + 1} to {Math.min(offset + limit, total)} of {total} calls
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setOffset(Math.max(0, offset - limit))}
              disabled={offset === 0}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setOffset(offset + limit)}
              disabled={offset + limit >= total}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Transcript Dialog */}
      <Dialog open={showTranscript} onOpenChange={setShowTranscript}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Call Transcript</DialogTitle>
            <DialogDescription>
              {selectedCall && (
                <>
                  {selectedCall.direction === 'inbound' ? 'Incoming' : 'Outgoing'} call
                  {getCallerDisplayName(selectedCall) ? ` from ${getCallerDisplayName(selectedCall)}` : ''}{' '}
                  ({formatPhoneNumber(selectedCall.phoneNumberFrom)}) - {formatCallTime(selectedCall.createdAt)}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[400px]">
            {selectedCall && (getCallerDisplayName(selectedCall) || selectedCall.metadata?.caseType || selectedCall.metadata?.urgency || selectedCall.callType) && (
              <div className="mb-4 p-3 bg-muted rounded-lg space-y-1">
                <h4 className="font-medium mb-2">Caller Details</h4>
                {getCallerDisplayName(selectedCall) && (
                  <p className="text-sm"><span className="text-muted-foreground">Name:</span> {getCallerDisplayName(selectedCall)}</p>
                )}
                {selectedCall.callType && (
                  <p className="text-sm"><span className="text-muted-foreground">Call Type:</span> {getCallTypeBadge(selectedCall.callType)}</p>
                )}
                {selectedCall.metadata?.caseType && (
                  <p className="text-sm"><span className="text-muted-foreground">Case Type:</span> {selectedCall.metadata.caseType}</p>
                )}
                {selectedCall.metadata?.urgency && (
                  <p className="text-sm"><span className="text-muted-foreground">Urgency:</span> {selectedCall.metadata.urgency}</p>
                )}
              </div>
            )}
            {selectedCall?.metadata?.transferBrief && (
              <div className="mb-4 p-3 rounded-lg border border-amber-500/40 bg-amber-50 dark:bg-amber-950/30">
                <div className="flex items-center gap-2 mb-1">
                  <PhoneForwarded className="h-4 w-4 text-amber-600" />
                  <h4 className="font-medium text-sm">Transfer brief</h4>
                </div>
                <p className="text-sm text-amber-900 dark:text-amber-100">{selectedCall.metadata.transferBrief}</p>
              </div>
            )}
            {selectedCall?.aiSummary ? (
              <div className="mb-4 p-3 bg-muted rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium">AI Summary</h4>
                  {selectedCall.qualificationScore != null && (
                    <div className="flex items-center gap-2">
                      {getScoreBadge(selectedCall.qualificationScore, selectedCall.sentiment)}
                      {selectedCall.sentiment && (
                        <Badge variant={selectedCall.sentiment === 'positive' ? 'default' : selectedCall.sentiment === 'negative' ? 'destructive' : 'secondary'}>
                          {selectedCall.sentiment.charAt(0).toUpperCase() + selectedCall.sentiment.slice(1)}
                        </Badge>
                      )}
                    </div>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">{selectedCall.aiSummary}</p>
              </div>
            ) : selectedCall && isProcessingInsights(selectedCall) ? (
              <div className="mb-4 p-3 bg-muted rounded-lg">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-sm font-medium">Generating AI insights...</span>
                </div>
              </div>
            ) : null}
            <div className="space-y-2">
              <h4 className="font-medium">Full Transcript</h4>
              {selectedCall && !selectedCall.transcriptionText && isProcessingInsights(selectedCall) ? (
                <div className="flex items-center gap-2 text-muted-foreground py-4">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-sm">Generating transcript and insights...</span>
                </div>
              ) : (
                <p className="text-sm whitespace-pre-wrap">
                  {selectedCall?.transcriptionText || 'No transcript available'}
                </p>
              )}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Dial Dialog */}
      <Dialog open={showDialDialog} onOpenChange={(open) => { if (!open) { setShowDialDialog(false); setDialNumber('') } }}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>New Call</DialogTitle>
            <DialogDescription>
              Enter a phone number to dial. This will open your default phone app.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <Input
              placeholder="+1 (555) 123 4567"
              value={dialNumber}
              onChange={(e) => setDialNumber(e.target.value)}
              autoComplete="off"
              autoFocus
              onKeyDown={(e) => {
                const digits = dialNumber.replace(/\D/g, '')
                if (e.key === 'Enter' && digits.length >= 10) {
                  const tel = digits.startsWith('1') ? `+${digits}` : `+1${digits}`
                  window.open(`tel:${tel}`, '_self')
                  setShowDialDialog(false)
                  setDialNumber('')
                }
              }}
            />
            <Button
              className="w-full"
              disabled={!dialNumber.trim() || dialNumber.replace(/\D/g, '').length < 10}
              onClick={() => {
                const digits = dialNumber.replace(/\D/g, '')
                if (digits.length < 10) return
                const tel = digits.startsWith('1') ? `+${digits}` : `+1${digits}`
                window.open(`tel:${tel}`, '_self')
                setShowDialDialog(false)
                setDialNumber('')
              }}
            >
              <PhoneCall className="mr-2 h-4 w-4" />
              Dial
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
